// The command palette's matching and its empty list (design-system plan, decision 15). Pure, so
// node --test checks it. A command lists itself with a short label, alias terms, a location hint
// and the groups it suggests itself in; this module ranks those entries against what was typed.
//
// A typed query ranks in three tiers. Tier 1 is the label: exact, a prefix of the whole label,
// each word of the query starting a word of the label in order, then a substring. Tier 2 is the
// aliases, by the same four forms. Tier 3, the close matches, is a one-letter typo in a label
// or alias and the query read as the starts of its words ("nus" for Next untranslated
// Segment); letters scattered across a label never qualify. Within a form, a command near the
// person's screen comes first, then a recent one, then the registry's order. A recent command
// therefore never outranks a better typed match.

/// What a command says about itself to the palette.
export type PaletteMeta<S> = { hidden: true } | PaletteListed<S>

export interface PaletteListed<S> {
  /// Short and plain; what the row shows and what a typed query matches first.
  label: string
  /// Other words people search for, such as "accept" for Confirm.
  aliases?: readonly string[]
  /// The trailing hint when the command's menu is not the right one, such as Organization.
  where?: string | ((shell: S) => string)
  /// A second line: the context or consequence, naming the selected project where one is acted on.
  note?: (shell: S) => string | undefined
  /// True where the command belongs to the person's screen, to break a tie between two matches.
  near?: (shell: S) => boolean
  /// Its place in the empty palette's group for a screen, by order; null or absent for none.
  suggest?: (shell: S, screen: Screen) => number | null
  /// Its place in the Home's Selected project group when a row is selected.
  forSelection?: number
}

export type Screen = 'home' | 'project'

/// One command as the palette sees it now: available, listed, and its metadata read.
export interface PaletteEntry {
  id: string
  label: string
  aliases: readonly string[]
  where: string | null
  note: string | null
  near: boolean
  /// Where it stands in the registry, the last tie-break.
  order: number
  /// Its order in the screen's group, null when it is not suggested there.
  suggested: number | null
  /// Its order in the Selected project group, null when it is not in it.
  selection: number | null
}

/// A ranked entry with the stretches of its label to draw in weight 600.
export interface Hit {
  entry: PaletteEntry
  spans: readonly (readonly [number, number])[]
}

export interface Results {
  matches: Hit[]
  close: Hit[]
}

export interface Section {
  heading: string
  hits: Hit[]
}

type Span = readonly [number, number]

/// The text a label is matched as: lower case, with a trailing ellipsis dropped. Offsets in the
/// label hold, since the ellipsis is last.
const plain = (label: string) => label.toLowerCase().replace(/…+$/, '')

const words = (text: string): { at: number; word: string }[] => [...text.matchAll(/\S+/g)].map(m => ({ at: m.index!, word: m[0] }))

/// How a query names a text, 0 for the whole text, 1 for its start, 2 when each word of the
/// query starts a word of the text in order, 3 for a stretch inside it; null for none.
function literal(query: string, text: string): { form: number; spans: Span[] } | null {
  if (text === query) return { form: 0, spans: [[0, text.length]] }
  if (text.startsWith(query)) return { form: 1, spans: [[0, query.length]] }
  const have = words(text)
  const spans: Span[] = []
  let from = 0
  const starts = query.split(' ').every(part => {
    while (from < have.length && !have[from]!.word.startsWith(part)) from += 1
    if (from >= have.length) return false
    spans.push([have[from]!.at, have[from]!.at + part.length])
    from += 1
    return true
  })
  if (starts) return { form: 2, spans }
  const inside = text.indexOf(query)
  return inside >= 0 ? { form: 3, spans: [[inside, inside + query.length]] } : null
}

/// Whether two strings differ by at most one letter added, dropped, changed or swapped with its
/// neighbour.
export function withinOneEdit(a: string, b: string): boolean {
  if (Math.abs(a.length - b.length) > 1) return false
  let i = 0
  while (i < a.length && i < b.length && a[i] === b[i]) i += 1
  if (i === a.length && i === b.length) return true
  if (a.length === b.length) {
    if (a.slice(i + 1) === b.slice(i + 1)) return true
    return a[i] === b[i + 1] && a[i + 1] === b[i] && a.slice(i + 2) === b.slice(i + 2)
  }
  const [long, short] = a.length > b.length ? [a, b] : [b, a]
  return long.slice(i + 1) === short.slice(i)
}

/// A one-letter slip in a stretch that starts a word of the text: the query read as typed,
/// with a letter added, dropped, changed or swapped. The first letter has to be right, as it
/// nearly always is, and a query shorter than five letters is more likely the start of a word than a slip in one.
function typo(query: string, text: string): Span[] | null {
  if (query.length < 5) return null
  const starts = [0, ...words(text).map(w => w.at)]
  for (const at of starts) {
    if (text[at] !== query[0]) continue
    for (const length of [query.length, query.length - 1, query.length + 1]) {
      if (at + length <= text.length && withinOneEdit(query, text.slice(at, at + length))) return [[at, at + length]]
    }
  }
  return null
}

/// The query read as the starts of the text's words, so "nus" finds Next untranslated Segment:
/// the fewest stretches, each the start of a later word than the one before. Null when it
/// cannot be read so.
function wordStarts(query: string, text: string): { chunks: number; spans: Span[] } | null {
  const letters = query.replace(/ /g, '')
  if (letters.length < 2) return null
  const have = words(text)
  const memo = new Map<number, { chunks: number; spans: Span[] } | null>()
  const best = (qi: number, wi: number): { chunks: number; spans: Span[] } | null => {
    if (qi === letters.length) return { chunks: 0, spans: [] }
    const key = qi * 1000 + wi
    if (memo.has(key)) return memo.get(key)!
    let found: { chunks: number; spans: Span[] } | null = null
    for (let w = wi; w < have.length; w++) {
      const word = have[w]!.word
      for (let size = Math.min(word.length, letters.length - qi); size >= 1; size--) {
        if (!word.startsWith(letters.slice(qi, qi + size))) continue
        const rest = best(qi + size, w + 1)
        if (rest && (found === null || rest.chunks + 1 < found.chunks)) found = { chunks: rest.chunks + 1, spans: [[have[w]!.at, have[w]!.at + size], ...rest.spans] }
      }
    }
    memo.set(key, found)
    return found
  }
  return best(0, 0)
}

/// What a query is to an entry: the tier, a rank within it (lower is better) and the label's
/// matched stretches.
interface Match {
  tier: 1 | 2 | 3
  rank: number
  spans: Span[]
}

function matchOf(query: string, entry: PaletteEntry): Match | null {
  const label = plain(entry.label)
  const own = literal(query, label)
  if (own) return { tier: 1, rank: own.form, spans: own.spans }
  let alias: number | null = null
  for (const term of entry.aliases) {
    const hit = literal(query, term.toLowerCase())
    if (hit && (alias === null || hit.form < alias)) alias = hit.form
  }
  if (alias !== null) return { tier: 2, rank: alias, spans: [] }
  const slip = typo(query, label)
  if (slip) return { tier: 3, rank: 0, spans: slip }
  for (const term of entry.aliases) if (typo(query, term.toLowerCase())) return { tier: 3, rank: 1, spans: [] }
  const starts = wordStarts(query, label)
  if (starts) return { tier: 3, rank: 1 + starts.chunks, spans: starts.spans }
  return null
}

const compare = (recent: readonly string[]) => (a: { entry: PaletteEntry }, b: { entry: PaletteEntry }): number => {
  const nearer = Number(b.entry.near) - Number(a.entry.near)
  if (nearer !== 0) return nearer
  const ra = recent.indexOf(a.entry.id)
  const rb = recent.indexOf(b.entry.id)
  if (ra !== rb) return (ra < 0 ? Infinity : ra) - (rb < 0 ? Infinity : rb)
  return a.entry.order - b.entry.order
}

/// The entries a typed query finds, best first: the label and alias matches, then the close ones
/// apart. `recent` is the person's recent command ids, newest first, used only to break ties.
export function search(entries: readonly PaletteEntry[], query: string, recent: readonly string[] = []): Results {
  const q = query.trim().toLowerCase().replace(/\s+/g, ' ')
  if (q === '') return { matches: [], close: [] }
  const found = entries.flatMap(entry => {
    const m = matchOf(q, entry)
    return m ? [{ entry, spans: m.spans, tier: m.tier, rank: m.rank }] : []
  })
  const tie = compare(recent)
  const ordered = (tier: 1 | 2 | 3) => found.filter(f => f.tier === tier).sort((a, b) => a.rank - b.rank || tie(a, b))
  const hit = ({ entry, spans }: { entry: PaletteEntry; spans: readonly Span[] }): Hit => ({ entry, spans })
  return { matches: [...ordered(1), ...ordered(2)].map(hit), close: ordered(3).map(hit) }
}

/// How many recent commands the empty palette shows.
export const RECENT_SHOWN = 3

export const HEADINGS = { recent: 'Recent', home: 'Home', project: 'In this project', selected: 'Selected project', close: 'Close matches' } as const

/// The empty palette: up to three recent commands that are still available, then the screen's
/// group, then on the Home the selected project's. A command shows once, in the first group
/// that has it. `heading` names the open project's group; the aligner's page names its session.
export function overview(entries: readonly PaletteEntry[], screen: Screen, recent: readonly string[], heading: string = HEADINGS.project): Section[] {
  const byId = new Map(entries.map(e => [e.id, e]))
  const shown = new Set<string>()
  const take = (list: PaletteEntry[]) => list.filter(e => !shown.has(e.id)).map(e => (shown.add(e.id), { entry: e, spans: [] as Span[] }))
  const sections: Section[] = []
  const recents = recent.flatMap(id => byId.get(id) ?? []).slice(0, RECENT_SHOWN)
  const push = (heading: string, hits: Hit[]) => {
    if (hits.length > 0) sections.push({ heading, hits })
  }
  push(HEADINGS.recent, take(recents))
  const group = entries.filter(e => e.suggested !== null).sort((a, b) => a.suggested! - b.suggested! || a.order - b.order)
  push(screen === 'home' ? HEADINGS.home : heading, take(group))
  if (screen === 'home') push(HEADINGS.selected, take(entries.filter(e => e.selection !== null).sort((a, b) => a.selection! - b.selection! || a.order - b.order)))
  return sections
}

/// Whether an Enter key is an input method confirming text, which the palette must not take as
/// "run the selected command". keyCode 229 is how a browser reports a key an IME has taken.
export function composing(e: { isComposing?: boolean; keyCode?: number }): boolean {
  return e.isComposing === true || e.keyCode === 229
}

/// The selected index after a move by `by` over `count` results: held inside the list, and -1
/// when the list is empty, so a later list starts clean.
export function moved(selected: number, by: number, count: number): number {
  return count === 0 ? -1 : Math.min(count - 1, Math.max(0, selected + by))
}

/// The selected index kept valid when the results change.
export function clamped(selected: number, count: number): number {
  return count === 0 ? -1 : Math.min(count - 1, Math.max(0, selected))
}
